import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'
import { API_URL } from '@/modules/common/config'
import { notifications, NotificationType, notify } from '@/components/common/NotificationPlugin'
import {
	isForbidden,
	isInternalServerError,
	isPreCondition,
	isUnauthorized,
	isGatewayTimeoutError,
	mapErrors,
} from '@/modules/common/utils/requestUtils'
import {
	authorizationHeaders,
	clientDashboardHeaders,
} from '@/modules/common/utils/apiRequestUtils'
import { RemoteLog } from '@/modules/common/utils/loggingUtils'
import store from '@/store'
import router from '@/router/router'

export interface Meta {
	current_page: number
	from: number
	last_page?: number
	path?: string
	per_page?: number
	to: number
	total?: number
}

export interface Links {
	first: string
	last: string
	prev: string | null
	next: string | null
	filters: string
}
export interface AttributeMeta {
	authorizedToShow: boolean
	authorizedToStore: boolean
	authorizedToUpdate: boolean
	authorizedToDelete: boolean
}

export interface Data<T> {
	id: string
	type: string
	attributes: T
	relationships: Record<any, any> | null
	meta: AttributeMeta
}

export interface DataList<T> {
	data: Array<Data<T>>
}

export interface AxiosList<T> {
	meta: Meta
	links: Links
	data: Array<Data<T>>
}

export interface RequestParams {
	page?: number
	perPage?: number
	related?: string
	sort?: string
	[key: string]: any
}

export const statusCodesToHandle = [400, 401, 422]

axios.defaults.baseURL = String(API_URL)

export function requestInterceptor(config: AxiosRequestConfig): AxiosRequestConfig {
	if (!config.headers) {
		config.headers = {}
	}

	config.headers.Accept = 'application/vnd.api+json'
	Object.assign(config.headers, clientDashboardHeaders())

	if (!config.headers['Authorization']) {
		Object.assign(config.headers, authorizationHeaders())
	}

	if (!config.headers['Split-User']) {
		let userUuid = store.state.auth?.user?.uuid
		if (window.analytics?.intialized === true) {
			userUuid =
				window.analytics?.user()?.anonymousId() ??
				window.localStorage.getItem('ajs_anonymous_id')?.replaceAll(/['"]/g, '')
		}
		const splitUser =
			new URL(window.location.href.toLowerCase()).searchParams.get('split_user') ??
			window.localStorage.getItem('split_user') ??
			userUuid

		if (splitUser !== null) {
			config.headers['Split-User'] = splitUser
		}
	}
	return config
}

axios.interceptors.request.use(requestInterceptor)

export function successInterceptor(response: AxiosResponse): AxiosResponse {
	return response.data
}

export interface CustomAxiosError extends AxiosError {
	handled: boolean
	errors: any
	status: number
}

/**
 * Opt a request out of the global error toast, for a caller that renders the failure
 * itself — a field-level validation message, say. Such a caller gets an unhandled
 * rejection and has to catch it.
 */
export function showsErrorsItself(error: CustomAxiosError): boolean {
	return (error.config as { showsErrorsItself?: boolean } | undefined)?.showsErrorsItself === true
}

let isEndingExpiredSession = false

/**
 * A page load fires several requests at once, so one expired session returns a 401 to each of
 * them. Only the first may tear the store down: the others would re-enter logout while it is
 * still awaiting, and resolve against state it has already cleared.
 */
async function endExpiredSession(): Promise<void> {
	if (isEndingExpiredSession) {
		return
	}

	isEndingExpiredSession = true
	RemoteLog.info({
		message: 'Unauthenticated response handled',
		context: { outcome: 'logged_out' },
	})

	try {
		await store.dispatch('auth/logout')
		await router.push('/login')
	} finally {
		isEndingExpiredSession = false
	}
}

// Ids, tokens and emails in a path all read as `{id}`, so the path is safe to log and counts each endpoint once.
function endpointOf(url: string | undefined): string | undefined {
	return url
		?.split('?')[0]
		.split('/')
		.map(segment => (/^[a-z_-]*$/.test(segment) ? segment : '{id}'))
		.join('/')
}

// The AD logs every request it answers; offline, timed out or refused by the WAF without CORS headers, only the browser knows.
function logUnansweredRequest(error: CustomAxiosError): void {
	if (error.code === AxiosError.ERR_CANCELED) {
		return
	}

	RemoteLog.warning({
		message: 'API request got no response',
		context: {
			method: error.config?.method,
			endpoint: endpointOf(error.config?.url),
			code: error.code,
			online: navigator.onLine,
		},
	})
}

export async function errorInterceptor(error: CustomAxiosError) {
	if (!error.response) {
		logUnansweredRequest(error)

		return Promise.reject(error)
	}

	const { status } = error.response
	let errors = ''
	if (statusCodesToHandle.includes(status)) {
		errors = mapErrors(error.response.data)
		if (errors === 'Unauthenticated.' || isUnauthorized(status)) {
			// Every 401 is resolved here, by redirect. Marked before the branches because the
			// notification below only fires for the first of a burst, and the rest are handled too.
			error.handled = true

			// A public page serves visitors without an account, so one whose session has died carries on as a guest.
			if (router.currentRoute.value.meta.public === true) {
				RemoteLog.info({
					message: 'Unauthenticated response handled',
					context: { outcome: 'continued_as_guest' },
				})
				store.commit('auth/clearSession')
				error.status = status

				return Promise.reject(error)
			}

			if (store.state.auth.isLoggedIn && !store.state.auth.user.client) {
				// user is logged into Auth0, but has no client
				RemoteLog.info({
					message: 'Unauthenticated response handled',
					context: { outcome: 'no_client' },
				})
				notifications.state = []
				router.push('/unauthorized')
			} else {
				errors = 'Your session expired. Please login in again to use the application'
				await endExpiredSession()
			}
		}
		if (notifications.state.length === 0 && !showsErrorsItself(error)) {
			notify({
				type: NotificationType.Error,
				message: errors,
				once: true,
			})
			error.handled = true
		}
	}

	if (isPreCondition(status)) {
		errors = 'One time password confirmation is required in order to perform this action'
		if (notifications.state.length === 0) {
			notify({
				type: NotificationType.Error,
				message: errors,
				once: true,
			})
			error.handled = true
		}
		await router.push('/2fa-login')
	}

	if (isForbidden(status)) {
		errors = 'You are not allowed to perform this action'
		if (notifications.state.length === 0) {
			notify({
				type: NotificationType.Error,
				message: errors,
				once: true,
			})
			error.handled = true
		}
	}

	if (isInternalServerError(status)) {
		errors = 'A server error occurred during request execution'
		if (notifications.state.length === 0) {
			notify({
				type: NotificationType.Error,
				message: errors,
				once: true,
			})
			error.handled = true
		}
	}

	if (isGatewayTimeoutError(status)) {
		const gatewayTimeoutData = error.response.data as { message?: string } | undefined

		if (gatewayTimeoutData?.message) {
			errors = gatewayTimeoutData.message
		} else {
			errors = 'Could not connect to external service, please try again later.'
		}
	}

	error.errors = errors
	error.status = status

	return Promise.reject(error)
}

axios.interceptors.response.use(successInterceptor, errorInterceptor)
